import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Upload from "@/pages/Upload";
import { UserMenu } from "@/components/UserMenu";
import { useAuth } from "@/context/AuthContext";
import { normalizeLang } from "@/lib/i18n";
import { uploadPickStrings } from "@/lib/uploadI18n";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const SETUP_COPY = {
  en: {
    title: "Interview Session Setup",
    subtitle: "Upload your résumé, job description and select your session preferences.",
    step1Before: "Upload your résumé ",
    step1Bold: "in English",
    step1After: " in MS Word (.docx) or text-based PDF formats only.",
    reviewInstructions: "Before continuing, please review your résumé text below. Our built-in Privacy Scan has attempted to remove or generalize personal and identifying details from your uploaded résumé to help protect your privacy. You can also manually remove any information it may have missed by clicking in the text field, select the desired text and delete it. Once you are satisfied with the résumé text that will be sent for processing, click the checkbox below to continue to Step 3.",
    steps: [
      "Step 1 - Upload Résumé",
      "Step 2 - Post-Privacy Scan Résumé Text Review",
      "Step 3 - Job Description",
      "Step 4 - Other Session Settings",
    ],
  },
  "pt-BR": {
    title: "Configuração da sessão de entrevista",
    subtitle: "Envie seu currículo e a descrição da vaga e selecione suas preferências de sessão.",
    step1Before: "Envie seu currículo ",
    step1Bold: "em inglês",
    step1After: " apenas nos formatos MS Word (.docx) ou PDF baseado em texto.",
    reviewInstructions: "Antes de continuar, revise o texto do seu currículo abaixo. Nossa Verificação de Privacidade integrada tentou remover ou generalizar informações pessoais e identificáveis do currículo enviado para ajudar a proteger sua privacidade. Você também pode remover manualmente qualquer informação que possa ter sido mantida clicando no campo de texto, selecionando o conteúdo desejado e apagando-o. Quando estiver satisfeito com o texto do currículo que será enviado para processamento, marque a caixa abaixo para continuar para a Etapa 3.",
    steps: [
      "Etapa 1 - Enviar currículo",
      "Etapa 2 - Revisar o currículo após a verificação de privacidade",
      "Etapa 3 - Descrição da vaga",
      "Etapa 4 - Outras configurações da sessão",
    ],
  },
  "es-419": {
    title: "Configuración de la sesión de entrevista",
    subtitle: "Suba su currículum y la descripción del puesto y seleccione sus preferencias de sesión.",
    step1Before: "Suba su currículum ",
    step1Bold: "en inglés",
    step1After: " únicamente en formato MS Word (.docx) o PDF basado en texto.",
    reviewInstructions: "Antes de continuar, revise el texto de su currículum a continuación. Nuestro Análisis de Privacidad integrado intentó eliminar o generalizar información personal e identificable del currículum que subió para ayudar a proteger su privacidad. También puede eliminar manualmente cualquier información que haya quedado haciendo clic en el campo de texto, seleccionando el texto deseado y borrándolo. Cuando esté satisfecho con el texto del currículum que se enviará para procesamiento, marque la casilla de abajo para continuar al Paso 3.",
    steps: [
      "Paso 1 - Subir currículum",
      "Paso 2 - Revisar el currículum después del análisis de privacidad",
      "Paso 3 - Descripción del puesto",
      "Paso 4 - Otras configuraciones de la sesión",
    ],
  },
  "fr-CA": {
    title: "Configuration de la séance d’entrevue",
    subtitle: "Téléversez votre CV et la description du poste, puis sélectionnez vos préférences de séance.",
    step1Before: "Téléversez votre CV ",
    step1Bold: "en anglais",
    step1After: " uniquement au format MS Word (.docx) ou PDF texte.",
    reviewInstructions: "Avant de continuer, veuillez réviser le texte de votre CV ci-dessous. Notre Analyse de confidentialité intégrée a tenté de retirer ou de généraliser les renseignements personnels et identifiants de votre CV afin d’aider à protéger votre vie privée. Vous pouvez aussi supprimer manuellement toute information qui aurait été conservée en cliquant dans le champ de texte, en sélectionnant le texte souhaité et en le supprimant. Lorsque vous êtes satisfait du texte du CV qui sera envoyé pour traitement, cochez la case ci-dessous pour passer à l’Étape 3.",
    steps: [
      "Étape 1 - Téléverser le CV",
      "Étape 2 - Réviser le CV après l’analyse de confidentialité",
      "Étape 3 - Description du poste",
      "Étape 4 - Autres paramètres de la séance",
    ],
  },
};

export const ACTIVE_INTERVIEW_COPY = {
  en: {
    title: "Interview already in progress",
    body: "You already have an interview in progress. You can return to it, or end it and start a new interview. Ending the current interview will not restore the session already used; starting a new interview will use another session from today's allowance.",
    returnToInterview: "Return to Interview",
    endAndStart: "End Interview and Start New",
    cancel: "Cancel",
    ending: "Ending interview…",
    endError: "Could not end the current interview. Please try again.",
    startError: "The current interview was ended, but the new interview could not be started automatically. Please click Start my interview again.",
  },
  "pt-BR": {
    title: "Entrevista já em andamento",
    body: "Você já tem uma entrevista em andamento. Você pode voltar para ela ou encerrá-la e iniciar uma nova entrevista. Encerrar a entrevista atual não devolverá a sessão já utilizada; iniciar uma nova entrevista utilizará outra sessão do limite de hoje.",
    returnToInterview: "Voltar para a entrevista",
    endAndStart: "Encerrar e iniciar uma nova",
    cancel: "Cancelar",
    ending: "Encerrando entrevista…",
    endError: "Não foi possível encerrar a entrevista atual. Tente novamente.",
    startError: "A entrevista atual foi encerrada, mas a nova entrevista não pôde ser iniciada automaticamente. Clique em Iniciar minha entrevista novamente.",
  },
  "es-419": {
    title: "Ya hay una entrevista en curso",
    body: "Ya tiene una entrevista en curso. Puede volver a ella o finalizarla e iniciar una nueva entrevista. Finalizar la entrevista actual no restaurará la sesión ya utilizada; iniciar una nueva entrevista utilizará otra sesión del límite de hoy.",
    returnToInterview: "Volver a la entrevista",
    endAndStart: "Finalizar e iniciar una nueva",
    cancel: "Cancelar",
    ending: "Finalizando entrevista…",
    endError: "No se pudo finalizar la entrevista actual. Inténtelo de nuevo.",
    startError: "La entrevista actual terminó, pero la nueva entrevista no pudo iniciarse automáticamente. Vuelva a hacer clic en Iniciar mi entrevista.",
  },
  "fr-CA": {
    title: "Une entrevue est déjà en cours",
    body: "Vous avez déjà une entrevue en cours. Vous pouvez y retourner ou y mettre fin et commencer une nouvelle entrevue. Mettre fin à l’entrevue actuelle ne rétablira pas la session déjà utilisée; commencer une nouvelle entrevue utilisera une autre session de l’allocation d’aujourd’hui.",
    returnToInterview: "Retourner à l’entrevue",
    endAndStart: "Mettre fin et en commencer une nouvelle",
    cancel: "Annuler",
    ending: "Fin de l’entrevue…",
    endError: "Impossible de mettre fin à l’entrevue actuelle. Veuillez réessayer.",
    startError: "L’entrevue actuelle a pris fin, mais la nouvelle entrevue n’a pas pu démarrer automatiquement. Cliquez de nouveau sur Commencer mon entrevue.",
  },
};

export default function UploadSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = normalizeLang(user?.preferred_language);
  const copy = SETUP_COPY[lang] || SETUP_COPY.en;
  const activeCopy = ACTIVE_INTERVIEW_COPY[lang] || ACTIVE_INTERVIEW_COPY.en;
  const uploadCopy = uploadPickStrings(lang);
  const flowRef = useRef(null);
  const returnButtonRef = useRef(null);
  const [activeConflict, setActiveConflict] = useState(null);
  const [resolvingConflict, setResolvingConflict] = useState(false);

  // The legacy Upload component still contains the retired credit_balance
  // client-side gate. Access is now governed by the server-side entitlement
  // and daily-session allowance system, which already bypasses that legacy
  // credit check. Neutralize only the stale client guard until Upload.jsx is
  // fully migrated away from credit_balance.
  if (user && !user.is_admin && Number(user.credit_balance ?? 0) <= 0) {
    user.credit_balance = 1;
  }

  // POST /sessions deliberately rejects a second simultaneous interview with
  // a structured 409. Handle that one response here so the legacy Upload catch
  // does not strand the user behind its generic session-creation message.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const detail = error?.response?.data?.detail;
        if (
          error?.response?.status === 409 &&
          detail?.code === "active_interview_exists" &&
          detail?.active_session_id
        ) {
          setActiveConflict({ sessionId: detail.active_session_id });
          // Upload.jsx will create its legacy generic toast after this rejection
          // reaches its catch block. Dismiss it on the next task so the recovery
          // dialog is the single, accurate user-facing message.
          window.setTimeout(() => toast.dismiss(), 0);
        }
        return Promise.reject(error);
      },
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    if (!activeConflict) return undefined;
    const focusTimer = window.setTimeout(() => returnButtonRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !resolvingConflict) {
        setActiveConflict(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [activeConflict, resolvingConflict]);

  const returnToActiveInterview = () => {
    if (!activeConflict?.sessionId || resolvingConflict) return;
    navigate(`/interview/${activeConflict.sessionId}`);
  };

  const endActiveAndStartNew = async () => {
    const sessionId = activeConflict?.sessionId;
    if (!sessionId || resolvingConflict) return;
    setResolvingConflict(true);
    try {
      await api.post(`/sessions/${sessionId}/end-interview`);
      setActiveConflict(null);
      setResolvingConflict(false);

      // Re-use the already-completed Setup form instead of discarding the user's
      // reviewed résumé/JD/settings. Both desktop and mobile buttons call the
      // same legacy submit handler; click the first enabled one still in the DOM.
      window.setTimeout(() => {
        const root = flowRef.current;
        const buttons = Array.from(
          root?.querySelectorAll(
            '[data-testid="start-interview-button"], [data-testid="start-interview-button-mobile"]',
          ) || [],
        );
        const startButton =
          buttons.find((button) => button.offsetParent !== null && !button.disabled) ||
          buttons.find((button) => !button.disabled);
        if (startButton) {
          startButton.click();
        } else {
          toast.error(activeCopy.startError);
        }
      }, 50);
    } catch {
      setResolvingConflict(false);
      toast.error(activeCopy.endError);
    }
  };

  useEffect(() => {
    const root = flowRef.current;
    if (!root) return undefined;

    const findSteps = () => {
      const fileInput = root.querySelector('input[type="file"]');
      const step1 = fileInput?.closest("label") || null;
      const step2 = root.querySelector('[data-testid="resume-review-block"]');
      const jdTextarea = root.querySelector('[data-testid="job-description-textarea"]');
      const step3 = jdTextarea?.closest(".rounded-2xl") || null;
      const voiceFemale = root.querySelector('[data-testid="voice-female"]');
      const step4 = voiceFemale?.closest(".mt-8.rounded-2xl") || null;
      return { fileInput, step1, step2, step3, step4, jdTextarea };
    };

    const setStepState = (el, enabled, label) => {
      if (!el) return;
      el.classList.add("setup-step");
      el.classList.toggle("is-step-active", enabled);
      el.classList.toggle("is-step-disabled", !enabled);
      if (el.getAttribute("data-step-label") !== label) {
        el.setAttribute("data-step-label", label);
      }
      const ariaValue = enabled ? "false" : "true";
      if (el.getAttribute("aria-disabled") !== ariaValue) {
        el.setAttribute("aria-disabled", ariaValue);
      }
      if ("inert" in el && el.inert === enabled) {
        el.inert = !enabled;
      }
    };

    const sync = () => {
      const { fileInput, step1, step2, step3, step4, jdTextarea } = findSteps();
      const reviewTextarea = root.querySelector('[data-testid="resume-review-textarea"]');
      const reviewCheckbox = root.querySelector('[data-testid="resume-review-ack-checkbox"]');
      const reviewLabel = root.querySelector('[data-testid="resume-review-ack-label"]');
      const reviewInstructions = root.querySelector('[data-testid="resume-review-instructions"]');
      const desktopStartButton = root.querySelector('[data-testid="start-interview-button"]');
      const showQuestionsButton = root.querySelector('[data-testid="toggle-show-text"]');
      const hideQuestionsButton = root.querySelector('[data-testid="toggle-hide-text"]');
      const femaleVoiceButton = root.querySelector('[data-testid="voice-female"]');
      const maleVoiceButton = root.querySelector('[data-testid="voice-male"]');
      const sampleModeRadio = root.querySelector('input[name="jd-mode"][value="sample"]');

      const step1Complete = Boolean(reviewTextarea?.value?.trim()) && !reviewTextarea?.disabled;
      const step2Complete = step1Complete && Boolean(reviewCheckbox?.checked);
      const step3Complete = step2Complete && Boolean(jdTextarea?.value?.trim());

      setStepState(step1, true, copy.steps[0]);
      setStepState(step2, step1Complete, copy.steps[1]);
      setStepState(step3, step2Complete, copy.steps[2]);
      setStepState(step4, step3Complete, copy.steps[3]);

      if (step1) {
        let instruction = step1.querySelector(':scope > .setup-step-one-instruction');
        const expectedText = `${copy.step1Before}${copy.step1Bold}${copy.step1After}`;
        if (!instruction) {
          instruction = document.createElement('p');
          instruction.className = 'setup-step-one-instruction';
          instruction.append(
            document.createTextNode(copy.step1Before),
            Object.assign(document.createElement('strong'), { textContent: copy.step1Bold }),
            document.createTextNode(copy.step1After),
          );
          step1.prepend(instruction);
        } else if (instruction.textContent !== expectedText) {
          instruction.replaceChildren(
            document.createTextNode(copy.step1Before),
            Object.assign(document.createElement('strong'), { textContent: copy.step1Bold }),
            document.createTextNode(copy.step1After),
          );
        }

        const hintCandidate = step1.querySelector('.min-w-0.flex-1 > .mt-2.text-sm');
        if (hintCandidate?.textContent === uploadCopy.resumeHint) {
          hintCandidate.classList.add('setup-hidden-resume-hint');
        }

        // Keep the native file input keyboard-accessible while retaining the
        // existing visual treatment. The visible filename is decorative for
        // assistive technology because the same information is included in
        // the file input's accessible name.
        if (fileInput) {
          fileInput.classList.remove("hidden");
          fileInput.classList.add("sr-only");
          const fileName = step1.querySelector("span.truncate");
          const accessibleName = fileName?.textContent?.trim()
            ? `${copy.steps[0]}: ${fileName.textContent.trim()}`
            : copy.steps[0];
          fileInput.setAttribute("aria-label", accessibleName);
          if (fileName) {
            fileName.setAttribute("aria-hidden", "true");
            fileName.parentElement?.setAttribute("aria-hidden", "true");
          }
        }
      }

      if (reviewCheckbox && reviewLabel) {
        const checkboxId = "resume-review-ack-checkbox";
        reviewCheckbox.id = checkboxId;
        reviewLabel.setAttribute("for", checkboxId);
        const reviewText = reviewLabel.textContent?.replace(/^\s*\*\s*/, "").trim();
        if (reviewText) reviewCheckbox.setAttribute("aria-label", reviewText);
        const labelText = reviewLabel.querySelector(":scope > span");
        if (labelText) labelText.setAttribute("aria-hidden", "true");
      }

      if (jdTextarea) {
        const sampleMode = Boolean(sampleModeRadio?.checked);
        jdTextarea.disabled = false;
        jdTextarea.readOnly = sampleMode;
        jdTextarea.setAttribute("aria-readonly", String(sampleMode));
      }

      if (showQuestionsButton && hideQuestionsButton) {
        const showingQuestions = showQuestionsButton.className.includes("border-[color:var(--brand)]");
        showQuestionsButton.setAttribute("aria-pressed", String(showingQuestions));
        hideQuestionsButton.setAttribute("aria-pressed", String(!showingQuestions));
      }

      if (femaleVoiceButton && maleVoiceButton) {
        const femaleSelected = femaleVoiceButton.className.includes("border-[color:var(--brand)]");
        femaleVoiceButton.setAttribute("aria-pressed", String(femaleSelected));
        maleVoiceButton.setAttribute("aria-pressed", String(!femaleSelected));
      }

      if (step4) {
        step4.classList.add("setup-step-four");
        step4.querySelectorAll("svg").forEach((icon) => {
          icon.setAttribute("aria-hidden", "true");
          icon.setAttribute("focusable", "false");
        });
      }
      if (reviewInstructions && reviewInstructions.textContent !== copy.reviewInstructions) {
        reviewInstructions.textContent = copy.reviewInstructions;
      }

      const privacyNote = desktopStartButton?.parentElement?.nextElementSibling;
      if (privacyNote) privacyNote.classList.add("setup-hidden-privacy-note");

      const startButtons = root.querySelectorAll(
        '[data-testid="start-interview-button"], [data-testid="start-interview-button-mobile"]'
      );
      startButtons.forEach((button) => {
        const flowBlocked = !step3Complete;
        button.disabled = flowBlocked;
        button.classList.toggle("flow-start-blocked", flowBlocked);
        const ariaValue = flowBlocked ? "true" : "false";
        if (button.getAttribute("aria-disabled") !== ariaValue) {
          button.setAttribute("aria-disabled", ariaValue);
        }
        const dataValue = flowBlocked ? "true" : "false";
        if (button.dataset.flowBlocked !== dataValue) {
          button.dataset.flowBlocked = dataValue;
        }
      });
    };

    const blockPrematureStart = (event) => {
      const button = event.target.closest?.(
        '[data-testid="start-interview-button"], [data-testid="start-interview-button-mobile"]'
      );
      if (button?.dataset.flowBlocked === "true") {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener("input", sync, true);
    root.addEventListener("change", sync, true);
    root.addEventListener("click", blockPrematureStart, true);
    const timer = window.setInterval(sync, 250);
    sync();

    return () => {
      observer.disconnect();
      root.removeEventListener("input", sync, true);
      root.removeEventListener("change", sync, true);
      root.removeEventListener("click", blockPrematureStart, true);
      window.clearInterval(timer);
    };
  }, [copy, uploadCopy]);

  return (
    <div className="interview-setup-shell min-h-screen bg-[color:var(--bg-base)]">
      <header className="border-b border-[color:var(--border-stone)] bg-white">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-6 md:px-10 py-3.5 flex min-w-0 items-center gap-3 sm:gap-4">
          <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3 mr-auto">
            <img
              src="/brand/globe-logo.svg"
              alt="English Job Interview Simulator"
              className="h-[56px] w-[76px] sm:h-[63px] sm:w-[86px] shrink-0 object-contain"
            />
            <div className="hidden sm:block whitespace-nowrap text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand)] leading-tight">
              English Job Interview Simulator
            </div>
          </div>
          <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3">
            <UserMenu />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-8 pb-2">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--text-primary)]">
          {copy.title}
        </h1>
        <p className="mt-2 text-sm sm:text-base text-[color:var(--text-secondary)] leading-relaxed">
          {copy.subtitle}
        </p>
      </div>

      <div ref={flowRef} className="legacy-upload-setup">
        <Upload />
      </div>

      {activeConflict && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-5"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !resolvingConflict) {
              setActiveConflict(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="active-interview-conflict-title"
            aria-describedby="active-interview-conflict-description"
            className="w-full max-w-lg rounded-2xl bg-white p-6 sm:p-8 shadow-xl"
          >
            <h2
              id="active-interview-conflict-title"
              className="text-xl sm:text-2xl font-semibold tracking-tight text-[color:var(--text-primary)]"
            >
              {activeCopy.title}
            </h2>
            <p
              id="active-interview-conflict-description"
              className="mt-3 text-sm leading-relaxed text-[color:var(--text-secondary)]"
            >
              {activeCopy.body}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-3">
              <button
                ref={returnButtonRef}
                type="button"
                disabled={resolvingConflict}
                onClick={returnToActiveInterview}
                className="rounded-full bg-[color:var(--brand)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[color:var(--brand-hover)] disabled:opacity-50"
              >
                {activeCopy.returnToInterview}
              </button>
              <button
                type="button"
                disabled={resolvingConflict}
                onClick={endActiveAndStartNew}
                className="rounded-full border border-[#9a4534]/50 px-5 py-2.5 text-sm font-medium text-[#9a4534] hover:bg-[#9a4534]/5 disabled:opacity-50"
              >
                {resolvingConflict ? activeCopy.ending : activeCopy.endAndStart}
              </button>
              <button
                type="button"
                disabled={resolvingConflict}
                onClick={() => setActiveConflict(null)}
                className="rounded-full border border-[color:var(--border-stone)] px-5 py-2.5 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] disabled:opacity-50"
              >
                {activeCopy.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .legacy-upload-setup > div > header {
          display: none !important;
        }

        .legacy-upload-setup > div > main {
          max-width: 48rem !important;
          padding-top: 0 !important;
        }

        .legacy-upload-setup > div > main > .grid {
          display: block !important;
        }

        .legacy-upload-setup > div > main > .grid > section {
          width: 100% !important;
          max-width: 48rem !important;
          margin: 0 auto !important;
        }

        .legacy-upload-setup > div > main > .grid > aside {
          display: none !important;
        }

        .legacy-upload-setup [data-testid="upload-welcome-hi"],
        .legacy-upload-setup [data-testid="how-it-works"],
        .legacy-upload-setup [data-testid="resume-sanitized-banner"],
        .legacy-upload-setup .setup-hidden-resume-hint,
        .legacy-upload-setup .setup-hidden-privacy-note {
          display: none !important;
        }

        .legacy-upload-setup > div > main > .grid > section > p.text-sm.uppercase,
        .legacy-upload-setup > div > main > .grid > section > h1,
        .legacy-upload-setup > div > main > .grid > section > h1 + p {
          display: none !important;
        }

        .legacy-upload-setup > div > main > .grid > section > .mt-10.space-y-4 {
          margin-top: 1rem !important;
        }

        .setup-step {
          position: relative;
          transition: background-color 160ms ease, border-color 160ms ease, opacity 160ms ease;
        }

        .setup-step::before {
          content: attr(data-step-label);
          display: block;
          margin-bottom: 0.75rem;
          font-size: 0.7rem;
          line-height: 1rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--brand);
        }

        .setup-step-one-instruction {
          margin: 0 0 1rem 0;
          font-size: 0.875rem;
          line-height: 1.4rem;
          color: var(--text-secondary);
        }

        .setup-step-one-instruction strong {
          font-weight: 700;
          color: var(--text-primary);
        }

        .setup-step.is-step-active {
          background: white !important;
          opacity: 1;
        }

        .setup-step.is-step-disabled {
          background: var(--bg-surface-hover) !important;
          border-color: var(--border-stone) !important;
          opacity: 0.62;
          cursor: not-allowed !important;
          user-select: none;
        }

        .setup-step.is-step-disabled::before,
        .setup-step.is-step-disabled * {
          color: var(--text-secondary) !important;
        }

        .setup-step.is-step-disabled textarea,
        .setup-step.is-step-disabled button,
        .setup-step.is-step-disabled label,
        .setup-step.is-step-disabled [class*="bg-white"] {
          background: var(--bg-surface-hover) !important;
        }

        .setup-step-four > :nth-child(2) {
          display: none !important;
        }

        .flow-start-blocked {
          opacity: 0.4 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }

        [data-testid="resume-review-block"].setup-step,
        .legacy-upload-setup [data-testid="job-description-textarea"]:not(:disabled) {
          background: white;
        }

        [data-testid="resume-review-block"].setup-step {
          border: 1px solid var(--border-stone);
          border-radius: 1rem;
          padding: 1.5rem;
        }

        .legacy-upload-setup label.setup-step:has(input[type="file"]:focus-visible) {
          outline: 2px solid var(--brand);
          outline-offset: 2px;
        }

        @media (max-width: 640px) {
          .interview-setup-shell > header > div {
            width: 100% !important;
            max-width: 100% !important;
            padding-left: 0.75rem !important;
            padding-right: 0.75rem !important;
            gap: 0.5rem !important;
          }

          .interview-setup-shell > header > div > div:first-child {
            gap: 0.25rem !important;
          }

          .interview-setup-shell > header > div > div:first-child > div {
            display: none !important;
          }

          .interview-setup-shell > header > div > div:last-child,
          .interview-setup-shell > header [data-testid="user-menu-button"] {
            min-width: 0 !important;
          }

          .interview-setup-shell > header img {
            width: 4.75rem !important;
            height: 3.5rem !important;
          }

          .legacy-upload-setup > div > main {
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
          }
        }
      `}</style>
    </div>
  );
}
